import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/utils/api";
import { fmtCurrency, fmtDate } from "@/utils/format";
import { CARI } from "@/constants/testIds";
import { toast } from "sonner";
import { Search, Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Eye } from "lucide-react";

const FIYAT_LISTELERI = ["Perakende", "Esnaf", "Servis"];

const EMPTY_FORM = {
  kod: "", unvan: "", vergi_no: "", vergi_dairesi: "", telefon: "",
  email: "", adres: "", il: "", ilce: "", grup: "", fiyat_listesi: "Perakende",
  vade_gun: 30, bakiye: 0,
};

function FormField({ label, name, type = "text", required, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(name, type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)}
        required={required}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E] transition-colors"
      />
    </div>
  );
}

export default function CariList() {
  const [cariler, setCariler] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [fiyatFilter, setFiyatFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 20 };
      if (search) params.search = search;
      if (fiyatFilter) params.fiyat_listesi = fiyatFilter;
      const r = await api.get("/cariler/", { params });
      setCariler(r.data.items);
      setTotal(r.data.total);
      setPages(r.data.pages || 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [page, search, fiyatFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [search, fiyatFilter]);

  const openCreate = () => { setEditItem(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (c) => { setEditItem(c); setForm({ ...EMPTY_FORM, ...c }); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/cariler/${editItem.id}`, form);
        toast.success("Cari güncellendi");
      } else {
        await api.post("/cariler/", form);
        toast.success("Cari oluşturuldu");
      }
      setShowModal(false);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu cariyi silmek istediğinizden emin misiniz?")) return;
    try {
      await api.delete(`/cariler/${id}`);
      toast.success("Cari silindi");
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateField = useCallback((name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>Cari Yönetimi</h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} kayıt</p>
        </div>
        <button
          data-testid={CARI.createButton}
          onClick={openCreate}
          className="flex items-center gap-2 bg-[#0D1B3E] text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#172A58] active:scale-[0.98] transition-all"
        >
          <Plus size={15} /> Yeni Cari
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid={CARI.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari kodu veya ünvan ara..."
            className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E] transition-colors"
          />
        </div>
        <select
          value={fiyatFilter}
          onChange={(e) => setFiyatFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E]"
        >
          <option value="">Tüm Fiyat Listeleri</option>
          {FIYAT_LISTELERI.map((f) => <option key={f}>{f}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="table-container">
          <table data-testid={CARI.listTable} className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr>
                {["Kod", "Ünvan", "Telefon", "İl", "Fiyat Listesi", "Bakiye", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Yükleniyor...</td></tr>
              ) : cariler.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Kayıt bulunamadı</td></tr>
              ) : cariler.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{c.kod}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">{c.unvan}</td>
                  <td className="px-4 py-3 text-slate-600">{c.telefon || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.il || "-"}</td>
                  <td className="px-4 py-3">
                    {c.fiyat_listesi && (
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{c.fiyat_listesi}</span>
                    )}
                  </td>
                  <td className={`px-4 py-3 tabular-nums font-semibold ${(c.bakiye ?? 0) > 0 ? "text-red-600" : "text-green-600"}`}>
                    {fmtCurrency(c.bakiye ?? 0)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => navigate(`/cariler/${c.id}`)} className="text-slate-400 hover:text-[#0D1B3E] transition-colors" title="Detay">
                        <Eye size={15} />
                      </button>
                      <button onClick={() => openEdit(c)} className="text-slate-400 hover:text-blue-600 transition-colors" title="Düzenle">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => handleDelete(c.id)} className="text-slate-400 hover:text-red-500 transition-colors" title="Sil">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
          <span className="text-xs text-slate-500">{total} kayıttan {Math.min((page-1)*20+1, total)}-{Math.min(page*20, total)} gösteriliyor</span>
          <div className="flex items-center gap-1">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-40 transition-colors">
              <ChevronLeft size={15} />
            </button>
            <span className="text-xs text-slate-600 px-2">{page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="p-1.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-40 transition-colors">
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>
                {editItem ? "Cari Düzenle" : "Yeni Cari"}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Cari Kodu" name="kod" required value={form.kod} onChange={updateField} />
                <FormField label="Ünvan" name="unvan" required value={form.unvan} onChange={updateField} />
                <FormField label="Vergi No" name="vergi_no" value={form.vergi_no} onChange={updateField} />
                <FormField label="Vergi Dairesi" name="vergi_dairesi" value={form.vergi_dairesi} onChange={updateField} />
                <FormField label="Telefon" name="telefon" value={form.telefon} onChange={updateField} />
                <FormField label="E-Posta" name="email" type="email" value={form.email} onChange={updateField} />
                <FormField label="İl" name="il" value={form.il} onChange={updateField} />
                <FormField label="İlçe" name="ilce" value={form.ilce} onChange={updateField} />
                <FormField label="Grup" name="grup" value={form.grup} onChange={updateField} />
                <FormField label="Vade Gün" name="vade_gun" type="number" value={form.vade_gun} onChange={updateField} />
                <FormField label="Bakiye" name="bakiye" type="number" value={form.bakiye} onChange={updateField} />
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Fiyat Listesi</label>
                  <select
                    value={form.fiyat_listesi || "Perakende"}
                    onChange={(e) => setForm((p) => ({ ...p, fiyat_listesi: e.target.value }))}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20"
                  >
                    {FIYAT_LISTELERI.map((f) => <option key={f}>{f}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Adres</label>
                <textarea
                  value={form.adres || ""}
                  onChange={(e) => setForm((p) => ({ ...p, adres: e.target.value }))}
                  rows={2}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E]"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">İptal</button>
                <button data-testid={CARI.saveButton} type="submit" disabled={saving} className="px-4 py-2 text-sm rounded-md bg-[#0D1B3E] text-white hover:bg-[#172A58] disabled:opacity-60 transition-colors">
                  {saving ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
